import React from 'react';

import { Col, Form, Input } from 'antd';
import { MaskedInput } from 'antd-mask-input';
import { useIntl } from 'react-intl';

// Şimdilik yalnız TR IBAN'ı desteklenir. Ülke kuralları BE'de de (`Assert\Iban`, `Assert\Bic`) doğrulanır.
const TR_IBAN_PATTERN = /^TR\d{24}$/;
const IBAN_MASK = 'TR00 0000 0000 0000 0000 0000 00';
// `lazy`: boş alanda maske karakterleri değer olarak görünmez. `prepare`: küçük harfle yapıştırılan "tr" de kabul edilir.
const IBAN_MASK_OPTIONS = { lazy: true, prepare: (value) => value.toUpperCase() };
const SWIFT_PATTERN = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

/** Maske boşluklarını ve harf/rakam dışı karakterleri atar, harfleri büyütür; alan boşaltılırsa BE'de değeri silmek için `null` döner. */
const normalizeCode = (value) => value?.replace(/[^A-Za-z0-9]/g, '').toUpperCase() || null;

/** ISO 13616 mod-97 kontrol hanesi: harfler 10–35'e çevrilip, ilk dört karakter sona alınarak 97'ye bölümden kalan 1 olmalı. */
const hasValidIbanChecksum = (iban) => {
  const digits = (iban.slice(4) + iban.slice(0, 4)).replace(/[A-Z]/g, (letter) => letter.charCodeAt(0) - 55);
  return [...digits].reduce((remainder, digit) => (remainder * 10 + Number(digit)) % 97, 0) === 1;
};

const isValidTrIban = (iban) => TR_IBAN_PATTERN.test(iban) && hasValidIbanChecksum(iban);

/**
 * Kullanıcının ödeme için banka bilgileri: IBAN ve SWIFT/BIC. Her alan bir `Col` içinde döner;
 * çağıran taraf bunları kendi `Row`'una yerleştirir.
 *
 * Alanlar formda `iban` ve `swiftCode` adıyla, boşluksuz tutulur (BE `User.iban`, `User.swiftCode`). Boş bırakılan
 * alan `null` olur; hiç dokunulmayan alan form değerlerinde `undefined` kalır.
 *
 * @param props.span Her alanın `md` genişliği; dikey formlarda 24.
 * @param props.disabled Salt-okunur görüntüleme için.
 */
const BankAccountFields = ({ span = 12, disabled = false }) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <Col
        sm={24}
        md={span}
      >
        <Form.Item
          name="iban"
          label={formatMessage({ id: 'bankAccount.iban' })}
          normalize={normalizeCode}
          rules={[
            {
              validator: (_, value) =>
                !value || isValidTrIban(value) ? Promise.resolve() : Promise.reject(new Error(formatMessage({ id: 'bankAccount.ibanInvalid' }))),
            },
          ]}
          className="gx-mb-2"
        >
          <MaskedInput
            mask={IBAN_MASK}
            maskOptions={IBAN_MASK_OPTIONS}
            placeholder={IBAN_MASK}
            disabled={disabled}
          />
        </Form.Item>
      </Col>
      <Col
        sm={24}
        md={span}
      >
        <Form.Item
          name="swiftCode"
          label={formatMessage({ id: 'bankAccount.swiftCode' })}
          normalize={normalizeCode}
          rules={[{ pattern: SWIFT_PATTERN, message: formatMessage({ id: 'bankAccount.swiftCodeInvalid' }) }]}
          className="gx-mb-2"
        >
          <Input
            placeholder="TCZBTR2A"
            maxLength={11}
            disabled={disabled}
          />
        </Form.Item>
      </Col>
    </>
  );
};

export default BankAccountFields;
